import {
  Button,
  Col,
  Row,
  Space,
  Input,
  Table,
  TableProps,
  InputRef,
  Modal,
  Spin,
  Tag,
} from 'antd';
import { useCallback, useEffect, useRef, useState } from 'react';
import { AllChecksResponseType } from 'types/checks';
import { dateForDisplay } from 'libs/dateFormaterPetition';
import type { ColumnsType, ColumnType } from 'antd/es/table';
import { SearchOutlined } from '@ant-design/icons';
import type { FilterConfirmProps } from 'antd/es/table/interface';
import Highlighter from 'react-highlight-words';
import dayjs from 'dayjs';
import ModalReportContent from './ModalReportContent';
import { getChecksReport } from 'api/reports.api';
import { getChecksList } from 'api/checks.api';
import { ReportFilter } from '../elements/ReportFilter';
import { ReportFilters } from 'types/reports';
import {
  getEndOfDate,
  getStartEndOfMonth,
  getStartEndOfMonthDayjs,
  getStartOfDate,
} from 'hooks/getStartEndOfDate';
import { defaultContractType } from 'constants/ReportConstants';
import * as Sentry from '@sentry/react';
import { pageSize } from 'constants/global';
import notificationHook from 'hooks/notificationHook';
import statusMessages from 'statusMessages';
import { ApiError } from 'types';
import { doContractKindsList } from 'store/slices/contractKindSlice';
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks';
import { SliceStatus } from 'constants/enums/slices';
import { DeviceTag } from 'types/devices';

type ChecksListProps = {
  checks: AllChecksResponseType[];
  pages: number;
};

const ChecksList = ({ checks, pages }: ChecksListProps) => {
  const [searchText, setSearchText] = useState('');
  const [checksData, setChecksData] = useState<AllChecksResponseType[]>([]);
  const [searchedColumn, setSearchedColumn] = useState('');
  const [startDate, setStartDate] = useState<dayjs.Dayjs | null>(null);
  const [endDate, setEndDate] = useState<dayjs.Dayjs | null>(null);
  const searchInput = useRef<InputRef>(null);
  const [employee, setEmployee] = useState('');
  const [contractType, setContractType] = useState(defaultContractType);
  const [loadingTable, setLoadingTable] = useState(false);
  const [totalPages, setTotalPages] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const { startOfMonth, endOfMonth } = getStartEndOfMonth();
  const { dayjsStartOfMonth, dayjsEndOfMonth } = getStartEndOfMonthDayjs();
  const [open, setOpen] = useState(false);
  const [confirmLoading, setConfirmLoading] = useState(false);
  const dispatch = useAppDispatch();

  const { contractKinds, contractKindsSliceStatus } = useAppSelector(
    (state) => state.contractKind
  );

  useEffect(() => {
    if (contractKindsSliceStatus === SliceStatus.Empty) {
      dispatch(doContractKindsList());
    } else if (contractKindsSliceStatus === SliceStatus.Fulfilled) {
      setContractType(
        contractKinds.length > 0 ? contractKinds[0].name : defaultContractType
      );
    }
  }, [contractKindsSliceStatus, contractKinds, dispatch]);

  type DataIndex = keyof AllChecksResponseType;

  const tableProps: TableProps<AllChecksResponseType> = {
    bordered: true,
    tableLayout: 'fixed',
    size: 'small',
    pagination: {
      position: ['bottomCenter'],
      pageSize: pageSize,
      total: totalPages,
      onChange: (page) => {
        setCurrentPage(page);
      },
    },
    className: 'table-ps',
  };

  useEffect(() => {
    setChecksData(checks);
    setTotalPages(pages);
  }, [checks, pages]);

  useEffect(() => {
    setStartDate(dayjsStartOfMonth);
    setEndDate(dayjsEndOfMonth);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getFilterOptions = useCallback(() => {
    const filterOptions: ReportFilters = {
      from: startDate ? getStartOfDate(startDate) : startOfMonth,
      to: endDate ? getEndOfDate(endDate) : endOfMonth,
      employee: employee !== '' ? employee : undefined,
      contractType: contractType !== '' ? contractType : undefined,
    };
    return filterOptions;
  }, [contractType, employee, endDate, endOfMonth, startDate, startOfMonth]);

  const search = useCallback(() => {
    setLoadingTable(true);
    const filterOptions = getFilterOptions();
    getChecksList(filterOptions, currentPage, pageSize).then((res) => {
      setChecksData(res.data.data);
      setTotalPages(res.data.total);
      setLoadingTable(false);
    });
  }, [getFilterOptions, currentPage]);

  const updateChecks = () => {
    search();
  };

  useEffect(() => {
    search();
  }, [startDate, endDate, employee, contractType, search]);

  const showModal = () => {
    setOpen(true);
  };

  const generateChecksReport = async () => {
    setConfirmLoading(true);
    try {
      const filterOptions = getFilterOptions();
      const data = await getChecksReport(filterOptions);
      setConfirmLoading(false);
      setOpen(false);
      const fileName = `Reporte_chequeos_${filterOptions.contractType}_${filterOptions.from}_al_${filterOptions.to}.xlsx`;
      const archivoUrl = window.URL.createObjectURL(new Blob([data.data]));
      const link = document.createElement('a');
      link.href = archivoUrl;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
    } catch (error) {
      Sentry.captureException(error);
      setConfirmLoading(false);
      setOpen(false);
      const currentStatus = (error as ApiError).statusCode;
      notificationHook({
        message: statusMessages.reports[
          currentStatus ? currentStatus : 500
        ] as string,
        type: 'error',
      });
    }
  };

  const handleStartDateChange = (date: dayjs.Dayjs | null) => {
    setStartDate(date);
  };

  const handleEndDateChange = (date: dayjs.Dayjs | null) => {
    setEndDate(date);
  };

  const handleEmployeeChange = (employee: string) => {
    setEmployee(employee);
  };

  const handleContractTypeChange = (value: string) => {
    setContractType(value);
  };

  const handleSearch = (
    selectedKeys: string[],
    confirm: (param?: FilterConfirmProps) => void,
    dataIndex: DataIndex
  ) => {
    confirm();
    setSearchText(selectedKeys[0]);
    setSearchedColumn(dataIndex);
  };

  const handleReset = (clearFilters: () => void) => {
    clearFilters();
    setSearchText('');
  };

  const getColumnSearchProps = (
    dataIndex: DataIndex
  ): ColumnType<AllChecksResponseType> => ({
    filterDropdown: ({
      setSelectedKeys,
      selectedKeys,
      confirm,
      clearFilters,
      close,
    }) => (
      <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
        <Input
          ref={searchInput}
          placeholder={`Search ${dataIndex}`}
          value={selectedKeys[0] ? selectedKeys[0].toString() : ''}
          onChange={(e) =>
            setSelectedKeys(e.target.value ? [e.target.value] : [])
          }
          onPressEnter={() =>
            handleSearch(selectedKeys as string[], confirm, dataIndex)
          }
          style={{ marginBottom: 8, display: 'block' }}
        />
        <Space>
          <Button
            type="primary"
            onClick={() =>
              handleSearch(selectedKeys as string[], confirm, dataIndex)
            }
            icon={<SearchOutlined />}
            size="small"
            style={{ width: 90 }}
          >
            Search
          </Button>
          <Button
            onClick={() => clearFilters && handleReset(clearFilters)}
            size="small"
            style={{ width: 90 }}
          >
            Reset
          </Button>
          <Button
            type="link"
            size="small"
            onClick={() => {
              close();
            }}
          >
            close
          </Button>
        </Space>
      </div>
    ),
    filterIcon: (filtered: boolean) => (
      <SearchOutlined
        style={{ color: filtered ? 'var(--brand-primary-color)' : undefined }}
      />
    ),
    onFilter: (value, record) => {
      return String(record[dataIndex])
        .toLowerCase()
        .includes((value as string).toLowerCase());
    },
    onFilterDropdownOpenChange: (visible) => {
      if (visible) {
        setTimeout(() => searchInput.current?.select(), 100);
      }
    },
    render: (text) =>
      searchedColumn === dataIndex ? (
        <Highlighter
          highlightStyle={{
            backgroundColor: 'var(--highlight-surface-color)',
            color: 'var(--highlight-text-color)',
            padding: 0,
          }}
          searchWords={[searchText]}
          autoEscape
          textToHighlight={text ? text.toString() : ''}
        />
      ) : (
        text
      ),
  });

  const columns: ColumnsType<AllChecksResponseType> = [
    {
      title: 'ID Empleado',
      key: `idEmployee`,
      dataIndex: 'idEmployee',
      render: (idEmployee) => <>{idEmployee ?? 'No Registrado'}</>,
      sorter: (a, b) => Number(a.idEmployee) - Number(b.idEmployee),
      sortDirections: ['descend', 'ascend'],
      ...getColumnSearchProps('idEmployee'),
    },
    {
      title: 'Fecha y Hora',
      key: 'date',
      dataIndex: 'date',
      render: (date) => dateForDisplay(date),
      sorter: (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime(),
      sortDirections: ['descend', 'ascend'],
    },
    {
      title: 'Apellido(s)',
      key: 'lastName',
      dataIndex: 'lastName',
      ...getColumnSearchProps('lastName'),
    },
    {
      title: 'Nombre(s)',
      key: 'name',
      dataIndex: 'name',
      ...getColumnSearchProps('name'),
    },
    {
      title: 'Tipo de contrato',
      key: 'contractType',
      dataIndex: 'contractType',
    },
    {
      title: 'Dispositivo',
      key: 'device',
      dataIndex: 'device',
      render: (device, record) => {
        if (!record.deviceTags || record.deviceTags.length === 0) {
          if (device) {
            return device;
          } else {
            return 'No Registrado';
          }
        } else {
          return record.deviceTags.map((tag: DeviceTag) => (
            <Tag
              className="tag"
              key={tag._id}
              color={tag.tagColor}
              variant="solid"
            >
              {tag.tagName}
            </Tag>
          ));
        }
      },
    },
  ];

  const resetFilters = () => {
    setStartDate(dayjsStartOfMonth);
    setEndDate(dayjsEndOfMonth);
    setEmployee('');
    setContractType(defaultContractType);
  };

  return (
    <>
      <Row>
        <Col xs={24}>
          <h2 className="title">Reporte de chequeos</h2>
          <ReportFilter
            startDate={startDate}
            endDate={endDate}
            employee={employee}
            contractType={contractType}
            handleStartDateChange={handleStartDateChange}
            handleEndDateChange={handleEndDateChange}
            updateData={updateChecks}
            resetFilters={resetFilters}
            showModal={showModal}
            handleEmployeeChange={handleEmployeeChange}
            handleContractTypeChange={handleContractTypeChange}
          />
          <Spin spinning={loadingTable} tip="Cargando...">
            <Table
              {...tableProps}
              dataSource={checksData}
              columns={columns}
              loading={loadingTable}
              id="checksTable"
              rowKey={'_id'}
            />
          </Spin>
        </Col>
      </Row>
      <Modal
        title="Descarga de Reporte"
        open={open}
        onOk={generateChecksReport}
        okText="Descargar"
        cancelText="Cancelar"
        destroyOnHidden
        confirmLoading={confirmLoading}
        onCancel={() => {
          setOpen(false);
        }}
      >
        <ModalReportContent
          startDate={startDate}
          endDate={endDate}
          employee={employee}
          contractType={contractType}
        />
      </Modal>
    </>
  );
};

export default ChecksList;
